/**
 * MODO SOFÁ — simulação local de 2 jogadores na mesma tela.
 *
 * Por que uma simulação própria, e não o pipeline do solo ou do Colyseus?
 * O resto do jogo assume UM jogador local: `playerTank`, mira, carga,
 * câmera e sessão são campos únicos espalhados por várias stores. Encaixar
 * um segundo jogador ali mexeria em solo, história, BR e multiplayer de uma
 * vez. Aqui o estado da partida inteira vive num objeto só, avançado por uma
 * função pura em passo fixo — testável no node, sem React, sem Three, sem
 * relógio de parede (todo tempo é `match.time`, em segundos de simulação).
 *
 * Reaproveita do jogo o que já é puro e compartilhado: gerador de obstáculos
 * e zonas de nascimento da arena multiplayer, colisão círculo × caixa e as
 * regras dos poderes de linhagem (shared/tankSpecialRules).
 *
 * Três modos:
 *  - coop:   os dois contra ondas de bots, chefe a cada 5. 3 vidas cada; quem
 *            zera pode ser resgatado pelo parceiro (ficar perto do sinal) ou
 *            volta no fim da onda. Acaba quando os dois estão fora juntos.
 *  - rivals: Disputa — as mesmas ondas, mas cada abate vale pontos para quem
 *            atirou. Após `rivalsWaves` ondas vence o maior placar.
 *  - versus: duelo. Renasce sempre; vence quem fizer `killsToWin` abates.
 */
import {
  circleIntersectsObstacle,
  generateMultiplayerObstacles,
  multiplayerSpawnZones,
  resolveCircleObstacleCollision,
  seededRandom,
} from "../../../../shared/multiplayerArena";
import {
  HOMING_ACQUIRE_RADIUS,
  HOMING_CONE_COS,
  PIERCE_FALLOFF,
  REGEN_DELAY_MS,
  STEALTH_ACTIVATION_MS,
  applyShieldReduction,
  getExplosiveRadius,
  getExplosiveSplashDamage,
  getHomingTurnRate,
  getPierceCount,
  getRegenPerSecond,
  getStealthDetectionRadius,
} from "../../../../shared/tankSpecialRules";
import {
  BOSS_EVERY,
  BOT_KINDS,
  BOT_UNLOCK_WAVE,
  DIFFICULTY,
  ITEM,
  ITEM_DURATION,
  ITEM_WEIGHTS,
  SOFA_ITEM_KINDS,
  type BotKind,
  type DifficultyTuning,
  type SofaDifficulty,
  type SofaItemKind,
} from "./sofaContent";

export type SofaMode = "coop" | "rivals" | "versus";
export type SofaPhase = "countdown" | "playing" | "intermission" | "over";
export type SeatIndex = 0 | 1 | 2 | 3;
/** Até quatro tanques no sofá: humanos (um controle cada) e CPUs. */
export const MAX_SOFA_PLAYERS = 4;
export type SofaCpuLevel = "easy" | "normal" | "hard";
export const SOFA_CPU_LEVELS: SofaCpuLevel[] = ["easy", "normal", "hard"];
export type { BotKind, SofaDifficulty, SofaItemKind };

/** Poderes de linhagem suportados no sofá (os das famílias de tanque). */
export type SofaSpecial = "shield" | "regen" | "stealth" | "explosive" | "piercing" | "homing";

export const SOFA = {
  STEP: 1 / 60,
  TANK_RADIUS: 1.1,
  PLAYER_SPEED: 12,
  /** Resposta da velocidade ao stick (1/s): alto = direto, baixo = derrapa. */
  PLAYER_ACCEL: 12,
  ROTATION_SPEED: 10,
  PLAYER_HEALTH: 100,
  COOP_LIVES: 3,
  COOP_RESPAWN: 3,
  VERSUS_RESPAWN: 2.5,
  INVULNERABLE: 2,
  // Tiro do jogador = o do solo (playerWeapon.ts): sem recarga mínima (cada
  // toque atira), carga cheia em 2 s contados do aperto, dano até 4x,
  // velocidade 25 (até 1.5x carregado) e 3 s de voo.
  FIRE_COOLDOWN: 0,
  /** Segurar o gatilho por mais que isso conta como tiro carregado. */
  CHARGE_DELAY: 0.15,
  /** Tempo, desde o aperto, até a carga cheia. */
  CHARGE_TIME: 2,
  BULLET_SPEED: 25,
  /** Dano do tanque inicial (KITSUNE-00); a linhagem ajusta em ±~10%. */
  BULLET_DAMAGE: 25,
  BULLET_SIZE: 0.25,
  BULLET_LIFE: 3,
  CHARGE_DAMAGE_MULT: 3,
  CHARGE_SPEED_MULT: 0.5,
  CHARGE_SIZE_BONUS: 0.5,
  /** Boca do cano: um pouco mais à frente no tiro carregado (bala maior). */
  MUZZLE: 1.5,
  MUZZLE_CHARGED: 2,
  /** Leque dos tiros múltiplos (metralhadora): abertura total e vão entre canos. */
  SPREAD: 0.35,
  BARREL_SPACING: 0.2,
  BOT_PREFERRED_DIST: 9,
  BOT_AIM_ERROR: 0.14,
  BOT_SPAWN_INTERVAL: 0.45,
  /** Tempo do portal: o bot aparece, mas só age (e só leva dano) depois. */
  BOT_SPAWN_TIME: 0.9,
  MAX_BOTS_ALIVE: 12,
  MAX_WAVE_BOTS: 26,
  COUNTDOWN: 3,
  INTERMISSION: 4,
  WAVE_HEAL: 35,
  VERSUS_KILLS: 5,
  KILLS_OPTIONS: [3, 5, 10] as readonly number[],
  RIVALS_WAVES: 5,
  RESCUE_RADIUS: 2.8,
  RESCUE_TIME: 2.5,
  RESCUE_HEALTH: 0.5,
  COOP_ARENA: 44,
  VERSUS_ARENA: 36,
  /** Espessura visual da parede — o tanque para antes dela. */
  WALL_INSET: 0.5,
} as const;

export interface SofaSeatInput {
  /** Movimento em espaço de tela: x → direita, y → baixo (= +Z no mundo). */
  moveX: number;
  moveY: number;
  /** Mira (stick direito). Magnitude baixa = mira segue o movimento. */
  aimX: number;
  aimY: number;
  fire: boolean;
  /** Usar o item guardado (LB/X no controle, E/Q no teclado — como nos outros modos). */
  useItem: boolean;
}

/** Efeito de item ativo (um por vez, como `activeEffect` dos outros modos). */
export interface SofaActiveEffect {
  kind: SofaItemKind;
  until: number;
  /** Campo de Força: tiros que ainda bloqueia. */
  hits: number;
}

export const NEUTRAL_INPUT: SofaSeatInput = {
  moveX: 0,
  moveY: 0,
  aimX: 0,
  aimY: 0,
  fire: false,
  useItem: false,
};

/** Perfil do tanque de cada assento: multiplicadores da linhagem + poder. */
export interface SofaSeatSetup {
  speedMul: number;
  damageMul: number;
  healthMul: number;
  special: SofaSpecial | null;
  specialPower: number;
  /** Presente = tanque controlado pela CPU, neste nível. */
  cpu?: SofaCpuLevel;
}

export const NEUTRAL_SEAT: SofaSeatSetup = {
  speedMul: 1,
  damageMul: 1,
  healthMul: 1,
  special: null,
  specialPower: 1,
};

export interface SofaObstacle {
  id: string;
  x: number;
  z: number;
  sizeX: number;
  sizeY: number;
  sizeZ: number;
  destructible: boolean;
  health: number;
  alive: boolean;
}

export interface SofaPlayer {
  seat: SeatIndex;
  setup: SofaSeatSetup;
  x: number;
  z: number;
  vx: number;
  vz: number;
  rotation: number;
  turret: number;
  health: number;
  maxHealth: number;
  alive: boolean;
  /** coop/rivals: vidas restantes (inclui a atual). versus: não usado. */
  lives: number;
  /** coop/rivals: sem vidas, esperando resgate ou o fim da onda. */
  eliminated: boolean;
  kills: number;
  deaths: number;
  score: number;
  shotsFired: number;
  shotsHit: number;
  damageDealt: number;
  /** Instante (match.time) do renascimento; 0 = nenhum agendado. */
  respawnAt: number;
  invulnerableUntil: number;
  lastShotAt: number;
  /** Instante em que o gatilho foi apertado; -1 = solto. */
  triggerDownAt: number;
  charge: number;
  lastHitAt: number;
  /** Item coletado aguardando uso (a Cura nunca fica guardada). */
  heldItem: SofaItemKind | null;
  effect: SofaActiveEffect | null;
  /** Atingido por tiro congelante: parado até aqui. */
  frozenUntil: number;
  /** Botão de item estava apertado no passo anterior (detecção de borda). */
  useItemDown: boolean;
  /** Poder stealth ativo neste passo (bots distantes perdem o alvo). */
  stealthed: boolean;
  /** Sinal de resgate: onde caiu ao ficar sem vidas. */
  rescueX: number;
  rescueZ: number;
  rescueProgress: number;
  /** Cérebro da CPU; null = humano (a entrada vem do controle). */
  cpu: SofaCpuBrain | null;
}

export interface SofaBot {
  id: number;
  kind: BotKind;
  radius: number;
  x: number;
  z: number;
  rotation: number;
  turret: number;
  health: number;
  maxHealth: number;
  /** Até aqui é só o portal: não se move, não atira, não leva dano. */
  activeAt: number;
  nextShotAt: number;
  burstLeft: number;
  /** Sniper: mira travada até este instante (laser visível); 0 = não está mirando. */
  aimingUntil: number;
  /** Comprimento do laser de mira (para a cena). */
  laserLength: number;
  patternIndex: number;
  /** +1/-1: lado para onde contorna o alvo quando está na distância boa. */
  strafeSign: number;
  /** Até quando ignora o alvo e anda em `detourAngle` (desatolar). */
  detourUntil: number;
  detourAngle: number;
  stuckFor: number;
  lastHitAt: number;
  /** Atingido por tiro congelante: parado e sem atirar até aqui. */
  frozenUntil: number;
}

export type BulletOwner = { kind: "player"; seat: SeatIndex } | { kind: "bot"; botKind: BotKind };

export interface SofaBullet {
  id: number;
  owner: BulletOwner;
  x: number;
  z: number;
  dx: number;
  dz: number;
  speed: number;
  damage: number;
  size: number;
  /** Dano em obstáculo destrutível (tiro carregado derruba de uma vez). */
  obstacleDamage: number;
  expiresAt: number;
  pierceLeft: number;
  /** Bots já atravessados por este projétil (perfuração não acerta duas vezes). */
  hitBots: number[];
  /** rad/s de curva rumo ao alvo (0 = reto). */
  homing: number;
  /** Poder do impacto explosivo (0 = sem explosão). */
  explosive: number;
  /** Já contou como acerto para a precisão do atirador. */
  counted: boolean;
  /** Tiro congelante (efeito ativo de quem atirou). */
  freeze: boolean;
}

export interface SofaItem {
  id: number;
  kind: SofaItemKind;
  x: number;
  z: number;
  spawnedAt: number;
  expiresAt: number;
}

export type SofaEvent =
  | { type: "go" }
  | {
      type: "shot";
      seat: SeatIndex | null;
      charge: number;
      x: number;
      z: number;
      botKind?: BotKind;
    }
  | {
      type: "hit";
      target: "player" | "bot" | "obstacle" | "wall";
      seat?: SeatIndex;
      by: SeatIndex | null;
      damage: number;
      x: number;
      z: number;
      shielded?: boolean;
    }
  | { type: "explosion"; x: number; z: number; big: boolean; splash?: boolean; boss?: boolean }
  | { type: "playerDown"; seat: SeatIndex; by: SeatIndex | null; eliminated: boolean }
  | { type: "playerRespawn"; seat: SeatIndex }
  | { type: "rescued"; seat: SeatIndex; by: SeatIndex }
  | { type: "botSpawn"; kind: BotKind; x: number; z: number }
  | { type: "botDown"; by: SeatIndex | null; kind: BotKind; x: number; z: number; score: number }
  | { type: "waveStart"; wave: number; boss: boolean }
  | { type: "waveClear"; wave: number; boss: boolean }
  | { type: "itemSpawn"; kind: SofaItemKind; x: number; z: number }
  | { type: "pickup"; seat: SeatIndex; kind: SofaItemKind; x: number; z: number }
  | { type: "itemUsed"; seat: SeatIndex; kind: SofaItemKind; x: number; z: number }
  | { type: "frozen"; x: number; z: number }
  | { type: "matchOver"; winner: SeatIndex | null };

export interface SofaMatch {
  mode: SofaMode;
  difficulty: SofaDifficulty;
  tuning: DifficultyTuning;
  seed: number;
  arenaSize: number;
  obstacles: SofaObstacle[];
  /** De 1 a 4 jogadores; `players[i].seat === i`. */
  players: SofaPlayer[];
  bots: SofaBot[];
  bullets: SofaBullet[];
  items: SofaItem[];
  phase: SofaPhase;
  time: number;
  phaseEndsAt: number;
  wave: number;
  bossWave: boolean;
  wavesCleared: number;
  bossesDefeated: number;
  spawnQueue: BotKind[];
  nextBotSpawnAt: number;
  nextItemAt: number;
  nextBulletId: number;
  nextBotId: number;
  nextItemId: number;
  killsToWin: number;
  rivalsWaves: number;
  /** versus/rivals: assento vencedor. coop e empate: null. */
  winner: SeatIndex | null;
  events: SofaEvent[];
  random: () => number;
}

export interface SofaMatchOptions {
  mode: SofaMode;
  seed?: number;
  killsToWin?: number;
  difficulty?: SofaDifficulty;
  rivalsWaves?: number;
  /** Um por jogador (1 a 4). Padrão: dois humanos neutros. */
  seats?: SofaSeatSetup[];
}

// ─── Utilidades ──────────────────────────────────────────────────────────────

function angleDiff(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

function turnToward(current: number, target: number, rate: number, dt: number): number {
  return current + angleDiff(current, target) * Math.min(1, rate * dt);
}

function clampMagnitude(x: number, y: number, max = 1): { x: number; y: number; mag: number } {
  const mag = Math.hypot(x, y);
  if (mag <= max) return { x, y, mag };
  return { x: (x / mag) * max, y: (y / mag) * max, mag: max };
}

function randRange(match: SofaMatch, min: number, max: number): number {
  return min + match.random() * (max - min);
}

/** Modos com ondas de bots (coop e Disputa). */
export function hasBots(mode: SofaMode): boolean {
  return mode !== "versus";
}

function spawnPoints(arenaSize: number) {
  return multiplayerSpawnZones(arenaSize);
}

function makePlayer(
  seat: SeatIndex,
  x: number,
  z: number,
  mode: SofaMode,
  setup: SofaSeatSetup
): SofaPlayer {
  const maxHealth = Math.round(SOFA.PLAYER_HEALTH * setup.healthMul);
  return {
    seat,
    setup,
    x,
    z,
    vx: 0,
    vz: 0,
    // Os dois começam olhando "para cima" da tela (−Z)
    rotation: Math.PI,
    turret: Math.PI,
    health: maxHealth,
    maxHealth,
    alive: true,
    lives: hasBots(mode) ? SOFA.COOP_LIVES : 0,
    eliminated: false,
    kills: 0,
    deaths: 0,
    score: 0,
    shotsFired: 0,
    shotsHit: 0,
    damageDealt: 0,
    respawnAt: 0,
    invulnerableUntil: SOFA.COUNTDOWN + SOFA.INVULNERABLE,
    lastShotAt: -Infinity,
    triggerDownAt: -1,
    charge: 0,
    lastHitAt: -Infinity,
    heldItem: null,
    effect: null,
    frozenUntil: 0,
    useItemDown: false,
    stealthed: false,
    rescueX: 0,
    rescueZ: 0,
    rescueProgress: 0,
    cpu: setup.cpu ? makeCpuBrain(setup.cpu, seat) : null,
  };
}

// ─── Criação ─────────────────────────────────────────────────────────────────

const COOP_START_PAIR: Array<[number, number]> = [
  [-2.5, 0],
  [2.5, 0],
];
const COOP_START_SQUAD: Array<[number, number]> = [
  [-2.5, -2],
  [2.5, -2],
  [-2.5, 2.5],
  [2.5, 2.5],
];
/** Zonas de nascimento do duelo, na ordem dos assentos. */
const VERSUS_START_ZONES = [1, 4, 2, 3];

export function createSofaMatch(options: SofaMatchOptions): SofaMatch {
  const mode = options.mode;
  const seed = options.seed ?? Math.floor(Math.random() * 1_000_000);
  const difficulty = options.difficulty ?? "normal";
  const arenaSize = hasBots(mode) ? SOFA.COOP_ARENA : SOFA.VERSUS_ARENA;
  const seats = (options.seats ?? [NEUTRAL_SEAT, NEUTRAL_SEAT]).slice(0, MAX_SOFA_PLAYERS);

  const obstacles: SofaObstacle[] = generateMultiplayerObstacles(seed, arenaSize).map((o) => ({
    id: o.id,
    x: o.x,
    z: o.z,
    sizeX: o.sizeX,
    sizeY: o.sizeY,
    sizeZ: o.sizeZ,
    destructible: o.type === "destructible",
    health: o.type === "destructible" ? o.health : Infinity,
    alive: true,
  }));

  const zones = spawnPoints(arenaSize);
  const players: SofaPlayer[] = seats.map((setup, i) => {
    const seat = i as SeatIndex;
    if (hasBots(mode)) {
      // Juntos no centro (zona central é livre de obstáculos)
      const spot = (seats.length <= 2 ? COOP_START_PAIR : COOP_START_SQUAD)[i];
      return makePlayer(seat, spot[0], spot[1], mode, setup);
    }
    // Duelo: cantos opostos primeiro (zonas 1 e 4), depois os outros dois
    const zone = zones[VERSUS_START_ZONES[i]];
    const p = makePlayer(seat, zone.x, zone.z, mode, setup);
    p.rotation = p.turret = Math.atan2(-zone.x, -zone.z);
    return p;
  });

  return {
    mode,
    difficulty,
    tuning: DIFFICULTY[difficulty],
    seed,
    arenaSize,
    obstacles,
    players,
    bots: [],
    bullets: [],
    items: [],
    phase: "countdown",
    time: 0,
    phaseEndsAt: SOFA.COUNTDOWN,
    wave: 0,
    bossWave: false,
    wavesCleared: 0,
    bossesDefeated: 0,
    spawnQueue: [],
    nextBotSpawnAt: 0,
    nextItemAt: SOFA.COUNTDOWN + ITEM.FIRST_SPAWN,
    nextBulletId: 1,
    nextBotId: 1,
    nextItemId: 1,
    killsToWin: options.killsToWin ?? SOFA.VERSUS_KILLS,
    rivalsWaves: options.rivalsWaves ?? SOFA.RIVALS_WAVES,
    winner: null,
    events: [],
    random: seededRandom(seed + 777),
  };
}

// ─── Consultas ───────────────────────────────────────────────────────────────

/**
 * Mais tanques do lado dos jogadores, mais bots na onda: +35% por jogador
 * além de dois (o balanceamento original é para dupla).
 */
export function squadScale(players: number): number {
  return 1 + 0.35 * (Math.max(1, players) - 2);
}

export function waveBotCount(
  wave: number,
  difficulty: SofaDifficulty = "normal",
  players = 2
): number {
  const scale = squadScale(players);
  return Math.min(
    Math.round(SOFA.MAX_WAVE_BOTS * Math.max(1, scale)),
    Math.max(2, Math.round((4 + (wave - 1) * 2) * DIFFICULTY[difficulty].botCount * scale))
  );
}

export function isBossWave(wave: number): boolean {
  return wave > 0 && wave % BOSS_EVERY === 0;
}

export function enemiesRemaining(match: SofaMatch): number {
  let alive = 0;
  for (const b of match.bots) if (b.health > 0) alive++;
  return alive + match.spawnQueue.length;
}

export function bossOf(match: SofaMatch): SofaBot | null {
  for (const b of match.bots) if (b.kind === "boss" && b.health > 0) return b;
  return null;
}

/** Jogador pode ser alvo/receber dano neste instante. */
function isTargetable(p: SofaPlayer): boolean {
  return p.alive && !p.eliminated;
}

function isBotActive(match: SofaMatch, b: SofaBot): boolean {
  return b.health > 0 && match.time >= b.activeAt;
}

function hasLineOfSight(match: SofaMatch, ax: number, az: number, bx: number, bz: number): boolean {
  const dist = Math.hypot(bx - ax, bz - az);
  const steps = Math.ceil(dist / 1);
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const x = ax + (bx - ax) * t;
    const z = az + (bz - az) * t;
    for (const obs of match.obstacles) {
      if (obs.alive && circleIntersectsObstacle(x, z, 0.15, obs)) return false;
    }
  }
  return true;
}

/** Distância livre a partir de (x,z) na direção `angle`, até `max` (laser do sniper). */
function rayLength(match: SofaMatch, x: number, z: number, angle: number, max: number): number {
  const dx = Math.sin(angle);
  const dz = Math.cos(angle);
  const limit = match.arenaSize / 2 - SOFA.WALL_INSET;
  for (let d = 0.5; d < max; d += 0.5) {
    const px = x + dx * d;
    const pz = z + dz * d;
    if (Math.abs(px) > limit || Math.abs(pz) > limit) return d;
    for (const obs of match.obstacles) {
      if (obs.alive && circleIntersectsObstacle(px, pz, 0.1, obs)) return d;
    }
  }
  return max;
}

// ─── Movimento compartilhado ─────────────────────────────────────────────────

function resolveStatic(match: SofaMatch, body: { x: number; z: number }, radius: number) {
  for (const obs of match.obstacles) {
    if (!obs.alive) continue;
    const r = resolveCircleObstacleCollision(body.x, body.z, radius, obs);
    body.x = r.x;
    body.z = r.z;
  }
  const limit = match.arenaSize / 2 - SOFA.WALL_INSET - radius;
  body.x = Math.max(-limit, Math.min(limit, body.x));
  body.z = Math.max(-limit, Math.min(limit, body.z));
}

/** Separa tanques sobrepostos (jogadores e bots) metade para cada lado. */
function separateTanks(match: SofaMatch) {
  const bodies: { x: number; z: number; r: number }[] = [];
  const refs: { x: number; z: number }[] = [];
  for (const p of match.players) {
    if (!p.alive) continue;
    bodies.push({ x: p.x, z: p.z, r: SOFA.TANK_RADIUS });
    refs.push(p);
  }
  for (const b of match.bots) {
    if (b.health <= 0) continue;
    bodies.push({ x: b.x, z: b.z, r: b.radius });
    refs.push(b);
  }
  for (let i = 0; i < bodies.length; i++) {
    for (let j = i + 1; j < bodies.length; j++) {
      const a = bodies[i];
      const b = bodies[j];
      const minDist = a.r + b.r;
      let dx = b.x - a.x;
      let dz = b.z - a.z;
      let d = Math.hypot(dx, dz);
      if (d >= minDist) continue;
      if (d < 1e-4) {
        dx = 1;
        dz = 0;
        d = 1;
      }
      const push = (minDist - d) / 2;
      a.x -= (dx / d) * push;
      a.z -= (dz / d) * push;
      b.x += (dx / d) * push;
      b.z += (dz / d) * push;
    }
  }
  for (let i = 0; i < bodies.length; i++) {
    resolveStatic(match, bodies[i], bodies[i].r);
    refs[i].x = bodies[i].x;
    refs[i].z = bodies[i].z;
  }
}

// ─── Jogadores ───────────────────────────────────────────────────────────────

/** Efeito ativo de um tipo, se ainda valendo. */
function activeEffect(
  match: SofaMatch,
  p: SofaPlayer,
  kind: SofaItemKind
): SofaActiveEffect | null {
  return p.effect && p.effect.kind === kind && match.time < p.effect.until ? p.effect : null;
}

function firePlayer(match: SofaMatch, p: SofaPlayer, charge: number) {
  const special = p.setup.special;
  const power = p.setup.specialPower;
  const minigun = !!activeEffect(match, p, "minigun");
  const itemMul = activeEffect(match, p, "intense")
    ? ITEM.INTENSE_DAMAGE_MULT
    : minigun
      ? ITEM.MINIGUN_DAMAGE_MULT
      : 1;
  // Como no solo: a metralhadora dobra os canos e o dano da rajada é
  // dividido entre eles (cobertura, não dano bruto)
  const shots = minigun ? ITEM.MINIGUN_SHOTS : 1;
  const total =
    SOFA.BULLET_DAMAGE * (1 + charge * SOFA.CHARGE_DAMAGE_MULT) * p.setup.damageMul * itemMul;
  const damage = Math.max(1, Math.round(total / shots));
  const muzzle = charge > 0 ? SOFA.MUZZLE_CHARGED : SOFA.MUZZLE;
  const dx = Math.sin(p.turret);
  const dz = Math.cos(p.turret);
  for (let i = 0; i < shots; i++) {
    const angle = shots > 1 ? p.turret + (i / (shots - 1) - 0.5) * SOFA.SPREAD : p.turret;
    const lateral = shots > 1 ? (i - (shots - 1) / 2) * SOFA.BARREL_SPACING : 0;
    const sx = Math.sin(angle);
    const sz = Math.cos(angle);
    match.bullets.push({
      id: match.nextBulletId++,
      owner: { kind: "player", seat: p.seat },
      // Cada tiro sai do seu cano: deslocado para o lado da torre
      x: p.x + sx * muzzle + dz * lateral,
      z: p.z + sz * muzzle - dx * lateral,
      dx: sx,
      dz: sz,
      speed: SOFA.BULLET_SPEED * (1 + charge * SOFA.CHARGE_SPEED_MULT),
      damage,
      size: SOFA.BULLET_SIZE + charge * SOFA.CHARGE_SIZE_BONUS,
      obstacleDamage: charge >= 0.5 ? 2 : 1,
      expiresAt: match.time + SOFA.BULLET_LIFE,
      pierceLeft: special === "piercing" ? getPierceCount(power) : 0,
      hitBots: [],
      homing: special === "homing" ? getHomingTurnRate(power) : 0,
      explosive: special === "explosive" ? power : 0,
      counted: false,
      freeze: !!activeEffect(match, p, "freeze"),
    });
  }
  p.lastShotAt = match.time;
  p.shotsFired += 1;
  match.events.push({
    type: "shot",
    seat: p.seat,
    charge,
    x: p.x + dx * muzzle,
    z: p.z + dz * muzzle,
  });
}

function stepPlayer(match: SofaMatch, p: SofaPlayer, input: SofaSeatInput, dt: number) {
  if (!p.alive) {
    p.vx = 0;
    p.vz = 0;
    p.triggerDownAt = -1;
    p.charge = 0;
    p.stealthed = false;
    return;
  }

  // Botão de item: aciona na borda (apertar), como nos outros modos
  if (input.useItem && !p.useItemDown) consumeHeldItem(match, p);
  p.useItemDown = input.useItem;

  // Congelado por tiro congelante (duelo): parado, sem atirar
  if (match.time < p.frozenUntil) {
    p.vx = 0;
    p.vz = 0;
    p.triggerDownAt = -1;
    p.charge = 0;
    return;
  }

  const speed =
    SOFA.PLAYER_SPEED * p.setup.speedMul * (activeEffect(match, p, "turbo") ? ITEM.TURBO_MULT : 1);
  const move = clampMagnitude(input.moveX, input.moveY);
  const k = Math.min(1, SOFA.PLAYER_ACCEL * dt);
  p.vx += (move.x * speed - p.vx) * k;
  p.vz += (move.y * speed - p.vz) * k;
  p.x += p.vx * dt;
  p.z += p.vz * dt;
  resolveStatic(match, p, SOFA.TANK_RADIUS);

  if (move.mag > 0.1) {
    p.rotation = turnToward(p.rotation, Math.atan2(move.x, move.y), SOFA.ROTATION_SPEED, dt);
  }

  const aimMag = Math.hypot(input.aimX, input.aimY);
  if (aimMag > 0.3) {
    p.turret = Math.atan2(input.aimX, input.aimY);
  } else if (move.mag > 0.1) {
    // Sem stick de mira (teclado, ou só movendo): a torre segue o movimento
    p.turret = Math.atan2(move.x, move.y);
  }

  // Gatilho: apertar arma, segurar carrega, soltar dispara.
  if (input.fire) {
    if (p.triggerDownAt < 0) p.triggerDownAt = match.time;
    const held = match.time - p.triggerDownAt;
    // Como no solo: abaixo do limiar é tiro normal; acima, a carga conta do aperto
    p.charge = held < SOFA.CHARGE_DELAY ? 0 : Math.min(1, held / SOFA.CHARGE_TIME);
  } else if (p.triggerDownAt >= 0) {
    const charge = p.charge;
    p.triggerDownAt = -1;
    p.charge = 0;
    if (match.time - p.lastShotAt >= SOFA.FIRE_COOLDOWN && match.phase !== "over") {
      firePlayer(match, p, charge);
    }
  }

  // Poderes passivos
  const special = p.setup.special;
  const power = p.setup.specialPower;
  p.stealthed = special === "stealth" && match.time - p.lastShotAt > STEALTH_ACTIVATION_MS / 1000;
  if (
    special === "regen" &&
    p.health < p.maxHealth &&
    match.time - p.lastHitAt > REGEN_DELAY_MS / 1000
  ) {
    p.health = Math.min(p.maxHealth, p.health + getRegenPerSecond(p.maxHealth, power) * dt);
  }
}

function pickRespawnPoint(match: SofaMatch, p: SofaPlayer): { x: number; z: number } {
  const zones = spawnPoints(match.arenaSize);
  const others = match.players.filter((o) => o !== p);

  let best = zones[0];
  let bestScore = -Infinity;
  for (const zone of zones) {
    let nearestBot = Infinity;
    for (const b of match.bots) {
      if (b.health > 0) nearestBot = Math.min(nearestBot, Math.hypot(b.x - zone.x, b.z - zone.z));
    }
    let nearestAlly = Infinity;
    let nearestRival = Infinity;
    for (const o of others) {
      const d = Math.hypot(o.x - zone.x, o.z - zone.z);
      if (isTargetable(o)) nearestAlly = Math.min(nearestAlly, d);
      nearestRival = Math.min(nearestRival, d);
    }
    let score: number;
    if (hasBots(match.mode)) {
      // Perto de um parceiro de pé, longe dos bots
      score = (nearestAlly < Infinity ? -nearestAlly : 0) + Math.min(nearestBot, 15) * 2;
    } else {
      // Duelo: o mais longe possível do adversário mais próximo
      score = nearestRival === Infinity ? 0 : nearestRival;
    }
    if (score > bestScore) {
      bestScore = score;
      best = zone;
    }
  }
  return { x: best.x, z: best.z };
}

function revive(match: SofaMatch, p: SofaPlayer, x: number, z: number, healthFraction: number) {
  p.x = x;
  p.z = z;
  resolveStatic(match, p, SOFA.TANK_RADIUS);
  p.vx = 0;
  p.vz = 0;
  p.alive = true;
  p.eliminated = false;
  p.health = Math.max(1, Math.round(p.maxHealth * healthFraction));
  p.respawnAt = 0;
  p.rescueProgress = 0;
  p.invulnerableUntil = match.time + SOFA.INVULNERABLE;
  p.lastHitAt = match.time;
  p.triggerDownAt = -1;
  p.charge = 0;
  p.effect = null;
  p.frozenUntil = 0;
}

function respawnPlayer(match: SofaMatch, p: SofaPlayer) {
  const point = pickRespawnPoint(match, p);
  revive(match, p, point.x, point.z, 1);
  match.events.push({ type: "playerRespawn", seat: p.seat });
}

/** true = o golpe foi absorvido pelo jogador (o projétil some). */
function damagePlayer(
  match: SofaMatch,
  p: SofaPlayer,
  rawDamage: number,
  by: SeatIndex | null
): boolean {
  if (!isTargetable(p) || match.phase === "over") return false;
  if (match.time < p.invulnerableUntil) return true;
  const shield = activeEffect(match, p, "shield");
  if (shield && shield.hits > 0) {
    shield.hits--;
    if (shield.hits <= 0) p.effect = null;
    match.events.push({
      type: "hit",
      target: "player",
      seat: p.seat,
      by,
      damage: 0,
      x: p.x,
      z: p.z,
      shielded: true,
    });
    return true;
  }
  const damage =
    p.setup.special === "shield"
      ? applyShieldReduction(rawDamage, p.setup.specialPower)
      : Math.round(rawDamage);
  const dealt = Math.min(p.health, damage);
  p.health = Math.max(0, p.health - damage);
  p.lastHitAt = match.time;
  if (by !== null) match.players[by].damageDealt += dealt;
  match.events.push({ type: "hit", target: "player", seat: p.seat, by, damage, x: p.x, z: p.z });
  if (p.health > 0) return true;

  p.alive = false;
  p.deaths++;
  p.triggerDownAt = -1;
  p.charge = 0;
  p.stealthed = false;
  p.effect = null;
  match.events.push({ type: "explosion", x: p.x, z: p.z, big: true });

  if (hasBots(match.mode)) {
    p.lives = Math.max(0, p.lives - 1);
    if (p.lives > 0) {
      p.respawnAt = match.time + SOFA.COOP_RESPAWN;
    } else {
      p.eliminated = true;
      p.respawnAt = 0;
      p.rescueX = p.x;
      p.rescueZ = p.z;
      p.rescueProgress = 0;
    }
    match.events.push({ type: "playerDown", seat: p.seat, by, eliminated: p.eliminated });
    if (match.players.every((pl) => pl.eliminated)) {
      endMatch(match, match.mode === "rivals" ? rivalsLeader(match) : null);
    }
  } else {
    p.respawnAt = match.time + SOFA.VERSUS_RESPAWN;
    match.events.push({ type: "playerDown", seat: p.seat, by, eliminated: false });
    if (by !== null) {
      const killer = match.players[by];
      killer.kills++;
      killer.score += 1;
      if (killer.kills >= match.killsToWin) endMatch(match, by);
    }
  }
  return true;
}

function rivalsLeader(match: SofaMatch): SeatIndex | null {
  let best: SofaPlayer | null = null;
  let tie = false;
  for (const p of match.players) {
    if (!best || p.score > best.score) {
      best = p;
      tie = false;
    } else if (p.score === best.score) {
      tie = true;
    }
  }
  return best && !tie ? best.seat : null;
}

function endMatch(match: SofaMatch, winner: SeatIndex | null) {
  if (match.phase === "over") return;
  match.phase = "over";
  match.winner = winner;
  match.phaseEndsAt = Infinity;
  match.events.push({ type: "matchOver", winner });
}

function stepRescue(match: SofaMatch, dt: number) {
  for (const p of match.players) {
    if (!p.eliminated) continue;
    // Qualquer parceiro de pé dentro do raio resgata
    let rescuer: SofaPlayer | null = null;
    let rescuerDist: number = SOFA.RESCUE_RADIUS;
    for (const other of match.players) {
      if (other === p || !isTargetable(other)) continue;
      const d = Math.hypot(other.x - p.rescueX, other.z - p.rescueZ);
      if (d < rescuerDist) {
        rescuerDist = d;
        rescuer = other;
      }
    }
    p.rescueProgress = rescuer ? p.rescueProgress + dt : Math.max(0, p.rescueProgress - dt * 0.5);
    if (rescuer && p.rescueProgress >= SOFA.RESCUE_TIME) {
      p.lives = 1;
      revive(match, p, p.rescueX, p.rescueZ, SOFA.RESCUE_HEALTH);
      match.events.push({ type: "rescued", seat: p.seat, by: rescuer.seat });
    }
  }
}

// ─── Bots ────────────────────────────────────────────────────────────────────

function pickWeighted<T extends string>(match: SofaMatch, weights: Array<[T, number]>): T {
  const total = weights.reduce((s, [, w]) => s + w, 0);
  let r = match.random() * total;
  for (const [value, w] of weights) {
    r -= w;
    if (r <= 0) return value;
  }
  return weights[weights.length - 1][0];
}

export function buildWaveQueue(match: SofaMatch, wave: number): BotKind[] {
  const total = waveBotCount(wave, match.difficulty, match.players.length);
  const queue: BotKind[] = [];
  let count = total;
  if (isBossWave(wave)) {
    queue.push("boss");
    count = Math.max(2, Math.round(total * 0.5));
  }
  const weights: Array<[BotKind, number]> = [];
  const base: Record<Exclude<BotKind, "boss">, number> = {
    normal: 4,
    fast: 3,
    heavy: 2,
    sniper: 2,
  };
  for (const kind of Object.keys(base) as Array<Exclude<BotKind, "boss">>) {
    if (wave >= BOT_UNLOCK_WAVE[kind]) weights.push([kind, base[kind]]);
  }
  for (let i = 0; i < count; i++) queue.push(pickWeighted(match, weights));
  return queue;
}

function botHealthFor(match: SofaMatch, kind: BotKind): number {
  const stats = BOT_KINDS[kind];
  const waveScale =
    kind === "boss"
      ? 1 + (Math.floor(match.wave / BOSS_EVERY) - 1) * 0.5
      : 1 + (match.wave - 1) * 0.1;
  // O chefe aguenta mais quando há mais canhões apontados para ele
  const squad = kind === "boss" ? Math.max(1, 1 + 0.5 * (match.players.length - 2)) : 1;
  return Math.round(stats.health * waveScale * match.tuning.botHealth * squad);
}

function spawnBot(match: SofaMatch, kind: BotKind): boolean {
  const stats = BOT_KINDS[kind];
  const zones =
    kind === "boss" ? spawnPoints(match.arenaSize).slice(5) : spawnPoints(match.arenaSize).slice(1);
  const scored = zones
    .map((zone) => {
      let nearestPlayer = Infinity;
      for (const p of match.players) {
        if (p.alive)
          nearestPlayer = Math.min(nearestPlayer, Math.hypot(p.x - zone.x, p.z - zone.z));
      }
      let crowd = 0;
      for (const b of match.bots) {
        if (b.health > 0 && Math.hypot(b.x - zone.x, b.z - zone.z) < b.radius + stats.radius + 0.5)
          crowd++;
      }
      return { zone, nearestPlayer, crowd };
    })
    .filter((s) => s.crowd === 0)
    .sort((a, b) => b.nearestPlayer - a.nearestPlayer);
  if (scored.length === 0) return false; // bordas ocupadas: tenta no próximo passo

  // Sorteia entre a metade mais distante dos jogadores
  const pool = scored.slice(0, Math.max(1, Math.ceil(scored.length / 2)));
  const { zone } = pool[Math.floor(match.random() * pool.length)];
  const health = botHealthFor(match, kind);
  const facing = Math.atan2(-zone.x, -zone.z);
  const activeAt = match.time + SOFA.BOT_SPAWN_TIME;
  const bot: SofaBot = {
    id: match.nextBotId++,
    kind,
    radius: stats.radius,
    x: zone.x,
    z: zone.z,
    rotation: facing,
    turret: facing,
    health,
    maxHealth: health,
    activeAt,
    nextShotAt: activeAt + 0.6 + match.random() * 0.8,
    burstLeft: 0,
    aimingUntil: 0,
    laserLength: 0,
    patternIndex: 0,
    strafeSign: match.random() < 0.5 ? -1 : 1,
    detourUntil: 0,
    detourAngle: 0,
    stuckFor: 0,
    lastHitAt: -Infinity,
    frozenUntil: 0,
  };
  resolveStatic(match, bot, bot.radius);
  match.bots.push(bot);
  match.events.push({ type: "botSpawn", kind, x: bot.x, z: bot.z });
  return true;
}

function botTarget(match: SofaMatch, bot: SofaBot): { target: SofaPlayer | null; dist: number } {
  let target: SofaPlayer | null = null;
  let targetDist = Infinity;
  for (const p of match.players) {
    if (!isTargetable(p)) continue;
    const d = Math.hypot(p.x - bot.x, p.z - bot.z);
    if (p.stealthed && d > getStealthDetectionRadius(p.setup.specialPower)) continue;
    if (d < targetDist) {
      targetDist = d;
      target = p;
    }
  }
  return { target, dist: targetDist };
}

function botBulletDamage(match: SofaMatch, kind: BotKind): number {
  const waveScale = 1 + Math.min(0.6, (match.wave - 1) * 0.05);
  return Math.round(BOT_KINDS[kind].damage * match.tuning.botDamage * waveScale);
}

function fireBot(match: SofaMatch, bot: SofaBot, angle: number) {
  const stats = BOT_KINDS[bot.kind];
  const dx = Math.sin(angle);
  const dz = Math.cos(angle);
  const muzzle = bot.radius + 0.5;
  match.bullets.push({
    id: match.nextBulletId++,
    owner: { kind: "bot", botKind: bot.kind },
    x: bot.x + dx * muzzle,
    z: bot.z + dz * muzzle,
    dx,
    dz,
    speed: stats.bulletSpeed,
    damage: botBulletDamage(match, bot.kind),
    size: stats.bulletSize,
    obstacleDamage: stats.obstacleDamage,
    expiresAt: match.time + (stats.range + 6) / stats.bulletSpeed,
    pierceLeft: 0,
    hitBots: [],
    homing: 0,
    explosive: 0,
    counted: false,
    freeze: false,
  });
}

function scheduleNextShot(match: SofaMatch, bot: SofaBot) {
  const stats = BOT_KINDS[bot.kind];
  const enraged = bot.kind === "boss" && bot.health < bot.maxHealth * 0.5;
  const interval =
    randRange(match, stats.fireMin, stats.fireMax) *
    match.tuning.fireInterval *
    (enraged ? 0.7 : 1);
  bot.nextShotAt = match.time + interval;
}

function stepBot(match: SofaMatch, bot: SofaBot, dt: number) {
  if (bot.health <= 0 || match.time < bot.activeAt) return;
  if (match.time < bot.frozenUntil) {
    bot.aimingUntil = 0;
    return;
  }
  const stats = BOT_KINDS[bot.kind];
  const { target, dist: targetDist } = botTarget(match, bot);
  const enraged = bot.kind === "boss" && bot.health < bot.maxHealth * 0.5;
  const speed = stats.speed * (enraged ? 1.2 : 1);
  const aiming = bot.aimingUntil > 0;

  let moveAngle: number | null = null;
  if (aiming) {
    moveAngle = null; // sniper fica parado enquanto mira
  } else if (match.time < bot.detourUntil) {
    moveAngle = bot.detourAngle;
  } else if (target) {
    const toTarget = Math.atan2(target.x - bot.x, target.z - bot.z);
    if (targetDist > stats.preferred + 3) moveAngle = toTarget;
    else if (targetDist < stats.preferred - 3) moveAngle = toTarget + Math.PI;
    else moveAngle = toTarget + (Math.PI / 2) * bot.strafeSign;
  }

  if (moveAngle !== null) {
    const beforeX = bot.x;
    const beforeZ = bot.z;
    bot.x += Math.sin(moveAngle) * speed * dt;
    bot.z += Math.cos(moveAngle) * speed * dt;
    resolveStatic(match, bot, bot.radius);
    bot.rotation = turnToward(bot.rotation, moveAngle, SOFA.ROTATION_SPEED * 0.6, dt);

    // Atolado num obstáculo: desvia por um tempo em ângulo aleatório
    const moved = Math.hypot(bot.x - beforeX, bot.z - beforeZ);
    if (moved < speed * dt * 0.25) {
      bot.stuckFor += dt;
      if (bot.stuckFor > 0.4) {
        bot.stuckFor = 0;
        bot.strafeSign = -bot.strafeSign;
        bot.detourAngle =
          moveAngle + (Math.PI / 2 + (match.random() * Math.PI) / 2) * bot.strafeSign;
        bot.detourUntil = match.time + 0.8 + match.random() * 0.6;
      }
    } else {
      bot.stuckFor = 0;
    }
  }

  // Rajada em andamento (veloz)
  if (bot.burstLeft > 0 && match.time >= bot.nextShotAt) {
    fireBot(
      match,
      bot,
      bot.turret + (match.random() * 2 - 1) * SOFA.BOT_AIM_ERROR * match.tuning.aimError
    );
    match.events.push({
      type: "shot",
      seat: null,
      charge: 0,
      x: bot.x,
      z: bot.z,
      botKind: bot.kind,
    });
    bot.burstLeft--;
    if (bot.burstLeft > 0) bot.nextShotAt = match.time + 0.15;
    else scheduleNextShot(match, bot);
    return;
  }

  if (!target) {
    bot.aimingUntil = 0;
    return;
  }
  const aim = Math.atan2(target.x - bot.x, target.z - bot.z);
  bot.turret = turnToward(bot.turret, aim, aiming ? 1.6 : 8, dt);

  // Sniper: mira travada com laser, depois o tiro
  if (aiming) {
    bot.laserLength = rayLength(match, bot.x, bot.z, bot.turret, stats.range);
    if (match.time >= bot.aimingUntil) {
      bot.aimingUntil = 0;
      fireBot(match, bot, bot.turret);
      match.events.push({
        type: "shot",
        seat: null,
        charge: 0.6,
        x: bot.x,
        z: bot.z,
        botKind: bot.kind,
      });
      scheduleNextShot(match, bot);
    }
    return;
  }

  const ready =
    match.time >= bot.nextShotAt &&
    targetDist < stats.range &&
    Math.abs(angleDiff(bot.turret, aim)) < 0.35 &&
    hasLineOfSight(match, bot.x, bot.z, target.x, target.z);
  if (!ready) return;

  if (stats.telegraph > 0) {
    bot.aimingUntil = match.time + stats.telegraph;
    bot.laserLength = rayLength(match, bot.x, bot.z, bot.turret, stats.range);
    return;
  }

  if (bot.kind === "boss") {
    if (bot.patternIndex % 2 === 0) {
      for (const offset of [-0.3, -0.1, 0.1, 0.3]) fireBot(match, bot, bot.turret + offset);
    } else {
      const ring = enraged ? 16 : 12;
      for (let i = 0; i < ring; i++) fireBot(match, bot, bot.turret + (i / ring) * Math.PI * 2);
    }
    bot.patternIndex++;
    match.events.push({
      type: "shot",
      seat: null,
      charge: 1,
      x: bot.x,
      z: bot.z,
      botKind: bot.kind,
    });
    scheduleNextShot(match, bot);
    return;
  }

  fireBot(
    match,
    bot,
    bot.turret + (match.random() * 2 - 1) * SOFA.BOT_AIM_ERROR * match.tuning.aimError
  );
  match.events.push({ type: "shot", seat: null, charge: 0, x: bot.x, z: bot.z, botKind: bot.kind });
  if (stats.burst > 1) {
    bot.burstLeft = stats.burst - 1;
    bot.nextShotAt = match.time + 0.15;
  } else {
    scheduleNextShot(match, bot);
  }
}

/** true = o bot morreu com este golpe. */
function damageBot(match: SofaMatch, bot: SofaBot, damage: number, by: SeatIndex | null): boolean {
  if (bot.health <= 0) return false;
  const dealt = Math.min(bot.health, damage);
  bot.health -= damage;
  bot.lastHitAt = match.time;
  if (by !== null) match.players[by].damageDealt += dealt;
  match.events.push({
    type: "hit",
    target: "bot",
    by,
    damage: Math.round(damage),
    x: bot.x,
    z: bot.z,
  });
  if (bot.health > 0) return false;

  bot.health = 0;
  if (bot.kind === "boss") match.bossesDefeated++;
  const score = BOT_KINDS[bot.kind].score;
  if (by !== null) {
    match.players[by].kills++;
    match.players[by].score += score;
  }
  match.events.push({
    type: "explosion",
    x: bot.x,
    z: bot.z,
    big: true,
    boss: bot.kind === "boss",
  });
  match.events.push({ type: "botDown", by, kind: bot.kind, x: bot.x, z: bot.z, score });
  return true;
}

function startWave(match: SofaMatch, wave: number) {
  match.wave = wave;
  match.bossWave = isBossWave(wave);
  match.spawnQueue = buildWaveQueue(match, wave);
  match.nextBotSpawnAt = match.time + 0.3;
  match.phase = "playing";
  match.phaseEndsAt = Infinity;
  match.events.push({ type: "waveStart", wave, boss: match.bossWave });
}

function clearWave(match: SofaMatch) {
  match.wavesCleared++;
  match.events.push({ type: "waveClear", wave: match.wave, boss: match.bossWave });
  if (match.mode === "rivals" && match.wave >= match.rivalsWaves) {
    endMatch(match, rivalsLeader(match));
    return;
  }
  match.phase = "intermission";
  match.phaseEndsAt = match.time + SOFA.INTERMISSION;
  for (const p of match.players) {
    if (p.eliminated) {
      // Resgate automático: volta com 1 vida no início da próxima onda
      p.eliminated = false;
      p.lives = 1;
      p.rescueProgress = 0;
      p.respawnAt = match.phaseEndsAt;
    } else if (p.alive) {
      p.health = Math.min(p.maxHealth, p.health + SOFA.WAVE_HEAL);
    }
  }
}

// ─── Itens ───────────────────────────────────────────────────────────────────

function findItemSpot(match: SofaMatch): { x: number; z: number } | null {
  const half = match.arenaSize / 2 - 3;
  for (let attempt = 0; attempt < 16; attempt++) {
    const x = randRange(match, -half, half);
    const z = randRange(match, -half, half);
    if (match.obstacles.some((o) => o.alive && circleIntersectsObstacle(x, z, 1.2, o))) continue;
    if (match.players.some((p) => p.alive && Math.hypot(p.x - x, p.z - z) < 4)) continue;
    if (match.bots.some((b) => b.health > 0 && Math.hypot(b.x - x, b.z - z) < 3)) continue;
    if (match.items.some((it) => Math.hypot(it.x - x, it.z - z) < 5)) continue;
    return { x, z };
  }
  return null;
}

function stepItems(match: SofaMatch) {
  // Expiração
  let write = 0;
  for (let i = 0; i < match.items.length; i++) {
    if (match.time < match.items[i].expiresAt) match.items[write++] = match.items[i];
  }
  match.items.length = write;

  // Nascimento
  if (match.time >= match.nextItemAt) {
    match.nextItemAt = match.time + randRange(match, ITEM.SPAWN_MIN, ITEM.SPAWN_MAX);
    if (match.items.length < ITEM.MAX_ON_FIELD) {
      const spot = findItemSpot(match);
      if (spot) {
        const kind = pickWeighted(
          match,
          SOFA_ITEM_KINDS.map((k) => [k, ITEM_WEIGHTS[k]] as [SofaItemKind, number])
        );
        match.items.push({
          id: match.nextItemId++,
          kind,
          ...spot,
          spawnedAt: match.time,
          expiresAt: match.time + ITEM.LIFETIME,
        });
        match.events.push({ type: "itemSpawn", kind, ...spot });
      }
    }
  }

  // Coleta: Cura vale na hora; os outros só entram se a mão estiver vazia
  // (senão ficam no chão para o parceiro — igual aos outros modos).
  for (const p of match.players) {
    if (!p.alive) continue;
    for (let i = 0; i < match.items.length; i++) {
      const item = match.items[i];
      if (Math.hypot(item.x - p.x, item.z - p.z) >= ITEM.PICKUP_RADIUS) continue;
      if (item.kind !== "heal" && p.heldItem) continue;
      if (item.kind === "heal") activateItem(match, p, "heal");
      else p.heldItem = item.kind;
      match.events.push({ type: "pickup", seat: p.seat, kind: item.kind, x: item.x, z: item.z });
      match.items.splice(i, 1);
      i--;
    }
  }
}

function consumeHeldItem(match: SofaMatch, p: SofaPlayer) {
  if (!p.heldItem || !p.alive) return;
  const kind = p.heldItem;
  p.heldItem = null;
  activateItem(match, p, kind);
  match.events.push({ type: "itemUsed", seat: p.seat, kind, x: p.x, z: p.z });
}

/** Aplica um item (Cura na hora; os outros viram o efeito ativo, substituindo o anterior). */
export function activateItem(match: SofaMatch, p: SofaPlayer, kind: SofaItemKind) {
  if (kind === "heal") {
    p.health = Math.min(p.maxHealth, p.health + ITEM.HEAL_AMOUNT);
    return;
  }
  p.effect = {
    kind,
    until: match.time + ITEM_DURATION[kind],
    hits: kind === "shield" ? ITEM.SHIELD_HITS : 0,
  };
}

// ─── Projéteis ───────────────────────────────────────────────────────────────

function steerHoming(match: SofaMatch, b: SofaBullet, dt: number) {
  if (b.homing <= 0 || b.owner.kind !== "player") return;
  const shooter = b.owner.seat;
  let bestX = 0;
  let bestZ = 0;
  let bestD = Infinity;
  const consider = (x: number, z: number) => {
    const tx = x - b.x;
    const tz = z - b.z;
    const d = Math.hypot(tx, tz);
    if (d > HOMING_ACQUIRE_RADIUS || d < 1e-3) return;
    if ((tx * b.dx + tz * b.dz) / d < HOMING_CONE_COS) return;
    if (d < bestD) {
      bestD = d;
      bestX = x;
      bestZ = z;
    }
  };
  if (hasBots(match.mode)) {
    for (const bot of match.bots)
      if (isBotActive(match, bot) && !b.hitBots.includes(bot.id)) consider(bot.x, bot.z);
  } else {
    for (const victim of match.players)
      if (victim.seat !== shooter && isTargetable(victim)) consider(victim.x, victim.z);
  }
  if (bestD === Infinity) return;
  const current = Math.atan2(b.dx, b.dz);
  const desired = Math.atan2(bestX - b.x, bestZ - b.z);
  const diff = angleDiff(current, desired);
  const maxTurn = b.homing * dt;
  const next = current + Math.max(-maxTurn, Math.min(maxTurn, diff));
  b.dx = Math.sin(next);
  b.dz = Math.cos(next);
}

function explode(match: SofaMatch, b: SofaBullet, x: number, z: number, skipBotId: number | null) {
  if (b.explosive <= 0 || b.owner.kind !== "player") return;
  const shooter = b.owner.seat;
  const radius = getExplosiveRadius(b.explosive);
  const splash = getExplosiveSplashDamage(b.damage, b.explosive);
  match.events.push({ type: "explosion", x, z, big: false, splash: true });
  if (hasBots(match.mode)) {
    for (const bot of match.bots) {
      if (bot.id === skipBotId || !isBotActive(match, bot)) continue;
      if (Math.hypot(bot.x - x, bot.z - z) <= radius + bot.radius)
        damageBot(match, bot, splash, shooter);
    }
  } else {
    for (const victim of match.players) {
      if (
        victim.seat !== shooter &&
        isTargetable(victim) &&
        Math.hypot(victim.x - x, victim.z - z) <= radius + SOFA.TANK_RADIUS
      ) {
        damagePlayer(match, victim, splash, shooter);
      }
    }
  }
}

function countHit(match: SofaMatch, b: SofaBullet) {
  if (b.counted || b.owner.kind !== "player") return;
  b.counted = true;
  match.players[b.owner.seat].shotsHit++;
}

function stepBullets(match: SofaMatch, dt: number) {
  const limit = match.arenaSize / 2 - SOFA.WALL_INSET;
  let write = 0;
  for (let read = 0; read < match.bullets.length; read++) {
    const b = match.bullets[read];
    steerHoming(match, b, dt);
    b.x += b.dx * b.speed * dt;
    b.z += b.dz * b.speed * dt;
    if (bulletCollides(match, b, limit)) continue;
    if (match.time >= b.expiresAt) continue;
    match.bullets[write++] = b;
  }
  match.bullets.length = write;
}

/** true = projétil consumido. */
function bulletCollides(match: SofaMatch, b: SofaBullet, limit: number): boolean {
  const by = b.owner.kind === "player" ? b.owner.seat : null;

  if (Math.abs(b.x) > limit || Math.abs(b.z) > limit) {
    match.events.push({ type: "hit", target: "wall", by, damage: 0, x: b.x, z: b.z });
    explode(match, b, b.x, b.z, null);
    return true;
  }

  for (const obs of match.obstacles) {
    if (!obs.alive || !circleIntersectsObstacle(b.x, b.z, b.size, obs)) continue;
    match.events.push({ type: "hit", target: "obstacle", by, damage: 0, x: b.x, z: b.z });
    if (obs.destructible) {
      obs.health -= b.obstacleDamage;
      if (obs.health <= 0) {
        obs.alive = false;
        match.events.push({ type: "explosion", x: obs.x, z: obs.z, big: false });
      }
    }
    explode(match, b, b.x, b.z, null);
    return true;
  }

  if (b.owner.kind === "bot") {
    for (const p of match.players) {
      if (!isTargetable(p)) continue;
      if (Math.hypot(p.x - b.x, p.z - b.z) < SOFA.TANK_RADIUS + b.size) {
        if (damagePlayer(match, p, b.damage, null)) return true;
      }
    }
    return false;
  }

  const shooter = b.owner.seat;
  if (match.mode === "versus") {
    // Todos contra todos: o primeiro tanque (que não o atirador) no caminho leva
    for (const victim of match.players) {
      if (victim.seat === shooter || !isTargetable(victim)) continue;
      if (Math.hypot(victim.x - b.x, victim.z - b.z) >= SOFA.TANK_RADIUS + b.size) continue;
      if (match.time >= victim.invulnerableUntil) countHit(match, b);
      const blocked =
        match.time < victim.invulnerableUntil || !!activeEffect(match, victim, "shield");
      damagePlayer(match, victim, b.damage, shooter);
      if (b.freeze && victim.alive && !blocked) {
        victim.frozenUntil = Math.max(victim.frozenUntil, match.time + ITEM.FREEZE_HIT_TIME);
        match.events.push({ type: "frozen", x: victim.x, z: victim.z });
      }
      explode(match, b, b.x, b.z, null);
      return true;
    }
    return false;
  }

  for (const bot of match.bots) {
    if (!isBotActive(match, bot) || b.hitBots.includes(bot.id)) continue;
    if (Math.hypot(bot.x - b.x, bot.z - b.z) >= bot.radius + b.size) continue;
    countHit(match, b);
    damageBot(match, bot, b.damage, shooter);
    if (b.freeze && bot.health > 0) {
      bot.frozenUntil = Math.max(bot.frozenUntil, match.time + ITEM.FREEZE_HIT_TIME);
      match.events.push({ type: "frozen", x: bot.x, z: bot.z });
    }
    explode(match, b, bot.x, bot.z, bot.id);
    if (b.pierceLeft > 0) {
      b.pierceLeft--;
      b.damage = Math.max(1, Math.round(b.damage * PIERCE_FALLOFF));
      b.hitBots.push(bot.id);
      continue;
    }
    return true;
  }
  return false;
}

// ─── Passo ───────────────────────────────────────────────────────────────────

// ─── Jogadores CPU ───────────────────────────────────────────────────────────
//
// Um assento sem humano é pilotado aqui. A CPU não trapaceia: produz a mesma
// entrada de um controle (mover, mirar, segurar/soltar o gatilho, item) e
// passa pelas mesmas regras, cooldowns e colisões do jogador. O nível muda
// reflexo, pontaria, cadência e se ela carrega o tiro.

interface CpuTuning {
  /** Intervalo entre "olhadas" (troca de erro de mira e de lado). */
  reaction: number;
  /** Erro máximo de mira (rad). */
  aimError: number;
  /** Pausa entre um disparo e o próximo aperto do gatilho. */
  fireGap: number;
  /** Fração do stick (velocidade). */
  moveScale: number;
  /** Segura o gatilho por isso para tiros longos (0 = só tiro rápido). */
  chargeHold: number;
  /** Alcance em que começa a atirar. */
  range: number;
  /** Quanto antecipa o movimento do alvo (0 = mira onde ele está). */
  lead: number;
}

const CPU_TUNING: Record<SofaCpuLevel, CpuTuning> = {
  easy: {
    reaction: 0.55,
    aimError: 0.3,
    fireGap: 0.9,
    moveScale: 0.7,
    chargeHold: 0,
    range: 16,
    lead: 0,
  },
  normal: {
    reaction: 0.32,
    aimError: 0.15,
    fireGap: 0.5,
    moveScale: 0.88,
    chargeHold: 0.5,
    range: 20,
    lead: 0.5,
  },
  hard: {
    reaction: 0.16,
    aimError: 0.06,
    fireGap: 0.3,
    moveScale: 1,
    chargeHold: 0.9,
    range: 24,
    lead: 0.9,
  },
};

export interface SofaCpuBrain {
  level: SofaCpuLevel;
  strafeSign: number;
  detourUntil: number;
  detourAngle: number;
  stuckFor: number;
  lastX: number;
  lastZ: number;
  nextThinkAt: number;
  aimOffset: number;
  /** Gatilho apertado até este instante; 0 = solto. Soltar dispara. */
  holdUntil: number;
  nextFireAt: number;
  itemPressed: boolean;
}

function makeCpuBrain(level: SofaCpuLevel, seat: SeatIndex): SofaCpuBrain {
  return {
    level,
    strafeSign: seat % 2 === 0 ? 1 : -1,
    detourUntil: 0,
    detourAngle: 0,
    stuckFor: 0,
    lastX: 0,
    lastZ: 0,
    nextThinkAt: 0,
    aimOffset: 0,
    holdUntil: 0,
    nextFireAt: SOFA.COUNTDOWN + 0.4 + seat * 0.15,
    itemPressed: false,
  };
}

interface CpuTarget {
  x: number;
  z: number;
  vx: number;
  vz: number;
  dist: number;
}

/** Inimigo mais próximo: bot ativo (coop/disputa) ou outro tanque (duelo). */
function cpuEnemy(match: SofaMatch, p: SofaPlayer): CpuTarget | null {
  let best: CpuTarget | null = null;
  const consider = (x: number, z: number, vx: number, vz: number) => {
    const dist = Math.hypot(x - p.x, z - p.z);
    if (!best || dist < best.dist) best = { x, z, vx, vz, dist };
  };
  if (hasBots(match.mode)) {
    for (const b of match.bots) if (isBotActive(match, b)) consider(b.x, b.z, 0, 0);
  } else {
    for (const o of match.players) {
      if (o === p || !isTargetable(o)) continue;
      const d = Math.hypot(o.x - p.x, o.z - p.z);
      if (o.stealthed && d > getStealthDetectionRadius(o.setup.specialPower)) continue;
      consider(o.x, o.z, o.vx, o.vz);
    }
  }
  return best;
}

function cpuInput(match: SofaMatch, p: SofaPlayer, brain: SofaCpuBrain, dt: number): SofaSeatInput {
  const tune = CPU_TUNING[brain.level];
  const input: SofaSeatInput = { ...NEUTRAL_INPUT };
  const moved = Math.hypot(p.x - brain.lastX, p.z - brain.lastZ);
  brain.lastX = p.x;
  brain.lastZ = p.z;
  if (!p.alive || match.phase === "countdown" || match.phase === "over") {
    brain.holdUntil = 0;
    return input;
  }

  if (match.time >= brain.nextThinkAt) {
    brain.nextThinkAt = match.time + tune.reaction;
    brain.aimOffset = (match.random() * 2 - 1) * tune.aimError;
    if (match.random() < 0.15) brain.strafeSign = -brain.strafeSign;
  }

  const enemy = cpuEnemy(match, p);

  // 1. Para onde ir: resgatar um parceiro > pegar item > combater
  let goal: { x: number; z: number } | null = null;
  if (hasBots(match.mode)) {
    let nearestDown = Infinity;
    for (const o of match.players) {
      if (o === p || !o.eliminated) continue;
      const d = Math.hypot(o.rescueX - p.x, o.rescueZ - p.z);
      if (d < nearestDown && (!enemy || enemy.dist > 7)) {
        nearestDown = d;
        goal = { x: o.rescueX, z: o.rescueZ };
      }
    }
  }
  if (!goal && (!enemy || enemy.dist > 10)) {
    let nearestItem = 12;
    for (const item of match.items) {
      const wanted = item.kind === "heal" ? p.health < p.maxHealth * 0.7 : p.heldItem === null;
      const d = Math.hypot(item.x - p.x, item.z - p.z);
      if (wanted && d < nearestItem) {
        nearestItem = d;
        goal = { x: item.x, z: item.z };
      }
    }
  }

  let angle: number | null = null;
  if (match.time < brain.detourUntil) {
    angle = brain.detourAngle;
  } else if (goal) {
    if (Math.hypot(goal.x - p.x, goal.z - p.z) > 0.6)
      angle = Math.atan2(goal.x - p.x, goal.z - p.z);
  } else if (enemy) {
    const preferred = SOFA.BOT_PREFERRED_DIST;
    const to = Math.atan2(enemy.x - p.x, enemy.z - p.z);
    if (enemy.dist > preferred + 4) angle = to;
    else if (enemy.dist < preferred - 3) angle = to + Math.PI;
    else angle = to + (Math.PI / 2) * brain.strafeSign;
  } else {
    // Nada à vista: fica perto do companheiro de pé mais próximo (ou do centro)
    let anchor = { x: 0, z: 0 };
    let anchorDist = Infinity;
    if (hasBots(match.mode)) {
      for (const o of match.players) {
        if (o === p || !isTargetable(o)) continue;
        const d = Math.hypot(o.x - p.x, o.z - p.z);
        if (d < anchorDist) {
          anchorDist = d;
          anchor = { x: o.x, z: o.z };
        }
      }
    }
    if (Math.hypot(anchor.x - p.x, anchor.z - p.z) > 5)
      angle = Math.atan2(anchor.x - p.x, anchor.z - p.z);
  }

  if (angle !== null) {
    input.moveX = Math.sin(angle) * tune.moveScale;
    input.moveY = Math.cos(angle) * tune.moveScale;
    // Atolado num obstáculo: contorna por um tempo, como os bots
    const expected = SOFA.PLAYER_SPEED * p.setup.speedMul * tune.moveScale * dt;
    if (match.time >= brain.detourUntil && moved < expected * 0.2) {
      brain.stuckFor += dt;
      if (brain.stuckFor > 0.35) {
        brain.stuckFor = 0;
        brain.strafeSign = -brain.strafeSign;
        brain.detourAngle =
          angle + (Math.PI / 2 + (match.random() * Math.PI) / 2) * brain.strafeSign;
        brain.detourUntil = match.time + 0.7 + match.random() * 0.5;
      }
    } else {
      brain.stuckFor = 0;
    }
  }

  // 2. Mira e gatilho (segurar → soltar dispara)
  if (enemy) {
    const flight = enemy.dist / SOFA.BULLET_SPEED;
    const tx = enemy.x + enemy.vx * flight * tune.lead;
    const tz = enemy.z + enemy.vz * flight * tune.lead;
    const aim = Math.atan2(tx - p.x, tz - p.z) + brain.aimOffset;
    input.aimX = Math.sin(aim);
    input.aimY = Math.cos(aim);

    if (brain.holdUntil > 0) {
      if (match.time < brain.holdUntil) input.fire = true;
      else {
        brain.holdUntil = 0;
        brain.nextFireAt = match.time + tune.fireGap;
      }
    } else if (
      match.time >= brain.nextFireAt &&
      enemy.dist <= tune.range &&
      hasLineOfSight(match, p.x, p.z, enemy.x, enemy.z)
    ) {
      const hold = enemy.dist > 10 ? tune.chargeHold : 0;
      brain.holdUntil = match.time + Math.max(SOFA.STEP * 2, hold);
      input.fire = true;
    }
  } else {
    brain.holdUntil = 0;
  }

  // 3. Item guardado: usa em combate (o botão age na borda)
  if (p.heldItem && enemy && enemy.dist < 14 && !brain.itemPressed) {
    input.useItem = true;
    brain.itemPressed = true;
  } else {
    brain.itemPressed = false;
  }

  return input;
}

/**
 * Avança a partida em `dt` segundos (use SOFA.STEP). Muta `match` e empilha
 * eventos em `match.events` — quem consome (áudio, efeitos) esvazia a fila.
 */
export function stepSofaMatch(
  match: SofaMatch,
  /** Entrada dos humanos, por assento. Assentos de CPU são ignorados. */
  inputs: readonly SofaSeatInput[],
  dt: number
) {
  match.time += dt;

  if (match.phase === "over") {
    // Balas em voo terminam o trajeto; ninguém mais se move.
    stepBullets(match, dt);
    return;
  }

  if (match.phase === "countdown") {
    if (match.time >= match.phaseEndsAt) {
      match.events.push({ type: "go" });
      if (hasBots(match.mode)) {
        startWave(match, 1);
      } else {
        match.phase = "playing";
        match.phaseEndsAt = Infinity;
      }
    }
    return;
  }

  if (match.phase === "intermission" && match.time >= match.phaseEndsAt) {
    startWave(match, match.wave + 1);
  }

  for (const p of match.players) {
    if (!p.alive && p.respawnAt > 0 && match.time >= p.respawnAt && !p.eliminated) {
      respawnPlayer(match, p);
    }
  }

  for (const p of match.players) {
    // CPU decide a própria entrada; humanos vêm do controle (posição = assento)
    const input = p.cpu ? cpuInput(match, p, p.cpu, dt) : (inputs[p.seat] ?? NEUTRAL_INPUT);
    stepPlayer(match, p, input, dt);
  }

  if (hasBots(match.mode)) {
    stepRescue(match, dt);
    if (
      match.phase === "playing" &&
      match.spawnQueue.length > 0 &&
      match.time >= match.nextBotSpawnAt
    ) {
      let alive = 0;
      for (const b of match.bots) if (b.health > 0) alive++;
      const maxAlive = SOFA.MAX_BOTS_ALIVE + 2 * Math.max(0, match.players.length - 2);
      if (alive < maxAlive && spawnBot(match, match.spawnQueue[0])) match.spawnQueue.shift();
      match.nextBotSpawnAt = match.time + SOFA.BOT_SPAWN_INTERVAL;
    }
    for (const bot of match.bots) stepBot(match, bot, dt);
  }

  separateTanks(match);
  stepItems(match);
  stepBullets(match, dt);

  // Remove os bots destruídos neste passo
  if (match.bots.some((b) => b.health <= 0)) match.bots = match.bots.filter((b) => b.health > 0);

  if (
    hasBots(match.mode) &&
    match.phase === "playing" &&
    match.spawnQueue.length === 0 &&
    match.bots.length === 0
  ) {
    clearWave(match);
  }
}

/**
 * Recompensa em chips da conta que está jogando (o dono do celular), ao fim
 * da partida. Pequena de propósito: o sofá é diversão local, não fazenda de
 * moeda — fica na mesma ordem de grandeza das ondas do modo solo.
 */
export function matchReward(match: SofaMatch): number {
  const kills = match.players.reduce((sum, p) => sum + p.kills, 0);
  if (match.mode === "versus") return Math.min(60, 20 + kills * 4);
  return Math.min(500, match.wavesCleared * 15 + kills * 2 + match.bossesDefeated * 50);
}

export function drainEvents(match: SofaMatch): SofaEvent[] {
  if (match.events.length === 0) return EMPTY_EVENTS;
  const events = match.events;
  match.events = [];
  return events;
}

const EMPTY_EVENTS: SofaEvent[] = [];
